import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaFileExcel,
  FaHistory,
  FaSearch,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import reporteHistorialHuespedApi from '../../../services/api/reporteHistorialHuespedApi';

function formatDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'object' && d.msg ? d.msg : JSON.stringify(d)))
      .join(' ');
  }
  return String(detail);
}

const estadoBadge = (estado) => {
  const e = (estado || '').toLowerCase();
  if (e === 'ocupada') return 'success';
  if (e === 'reservada') return 'warning';
  if (e === 'finalizada') return 'secondary';
  return 'info';
};

const filasExcel = (estadias) =>
  (estadias || []).map((e, idx) => ({
    '#': idx + 1,
    DNI: e.dni ?? '',
    Trabajador: e.nombre_completo ?? '',
    Zona: e.zona ?? '',
    Módulo: e.modulo ?? '',
    Habitación: e.habitacion ?? '',
    Cama: e.cama ?? '',
    'Fecha reserva': e.fecha_reserva_fmt ?? '—',
    'Check-in': e.fecha_check_in_fmt ?? '—',
    'Check-out': e.fecha_check_out_fmt ?? '—',
    'Días estadía': e.dias_estadia ?? '',
    Estado: e.estado_asignacion ?? '',
    Activo: e.activo ? 'Sí' : 'No',
  }));

const resumenPorDni = (estadias) => {
  const map = new Map();
  (estadias || []).forEach((e) => {
    const dni = e.dni || '';
    if (!map.has(dni)) {
      map.set(dni, {
        DNI: dni,
        Trabajador: e.nombre_completo ?? '',
        'Total registros': 0,
        'Con check-in': 0,
        'Con check-out': 0,
        'Días hospedado': 0,
        'Estadía activa': 'No',
      });
    }
    const row = map.get(dni);
    row['Total registros'] += 1;
    if (e.fecha_check_in) row['Con check-in'] += 1;
    if (e.fecha_check_out) row['Con check-out'] += 1;
    if (e.dias_estadia != null) row['Días hospedado'] += Number(e.dias_estadia) || 0;
    if (e.fecha_check_in && !e.fecha_check_out && e.activo) {
      row['Estadía activa'] = 'Sí';
    }
  });
  return Array.from(map.values());
};

const ReporteHistorialHuesped = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(
    localStorage.getItem('selectedProject') || 'null'
  );

  const [dni, setDni] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [estadias, setEstadias] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [generado, setGenerado] = useState(false);

  const generar = useCallback(
    async (e) => {
      e?.preventDefault?.();
      setError('');
      if (!selectedProject?.id) {
        setError('Seleccione un proyecto en el inicio de sesión.');
        return;
      }
      setLoading(true);
      try {
        const res = await reporteHistorialHuespedApi.consultar(
          selectedProject.id,
          dni
        );
        if (res?.success) {
          setEstadias(res.data?.estadias || []);
          setResumen(res.data?.resumen || null);
          setGenerado(true);
        } else {
          setError(res?.message || 'Respuesta inesperada del servidor.');
        }
      } catch (err) {
        const d =
          err.response?.data?.detail ??
          err.response?.data?.message ??
          err.message;
        setError(formatDetail(d) || 'No se pudo generar el reporte.');
      } finally {
        setLoading(false);
      }
    },
    [dni, selectedProject?.id]
  );

  const exportarExcel = () => {
    if (!estadias.length) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(filasExcel(estadias)),
      'Historial'
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(resumenPorDni(estadias)),
      'Resumen por DNI'
    );
    const slug = (selectedProject?.nombre || 'proyecto')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    XLSX.writeFile(wb, `historial_huesped_${slug || 'proyecto'}.xlsx`);
  };

  return (
    <>
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.75rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; filter: brightness(1.06); }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; }
          .dh-form-label { font-weight: 600; color: #374151; }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-alert-info {
            border-radius: 12px;
            border: none;
            background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
            color: #1e40af;
          }
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.85rem !important;
          }
          .dh-kpi {
            background: #f8fafc;
            border-radius: 12px;
            padding: 0.85rem 1rem;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <FaHistory size={36} />
                <div>
                  <h1 className="dh-main-title">Historial de huéspedes</h1>
                  <p className="dh-main-subtitle">
                    Mismas columnas que la consulta por DNI, para todo el proyecto
                    {selectedProject?.nombre
                      ? `: ${selectedProject.nombre}`
                      : ''}
                  </p>
                </div>
              </div>
              <Button
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate('/reportes')}
              >
                <FaArrowLeft className="me-2" /> Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">
              <FaSearch className="me-2" /> Generar reporte
            </Card.Header>
            <Card.Body className="p-4">
              <Alert variant="info" className="dh-alert dh-alert-info">
                Deje el DNI vacío para exportar el historial de todos los
                trabajadores del proyecto actual. El Excel incluye el detalle
                de estadías y un resumen por DNI.
              </Alert>
              <Form onSubmit={generar}>
                <Row className="align-items-end g-3">
                  <Col md={4}>
                    <Form.Label className="dh-form-label">
                      DNI (opcional)
                    </Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={dni}
                      onChange={(ev) => setDni(ev.target.value)}
                      placeholder="Todos los DNI del proyecto"
                      maxLength={20}
                    />
                  </Col>
                  <Col md="auto">
                    <Button
                      type="submit"
                      className="btn-dh-primary d-flex align-items-center"
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : (
                        <FaSearch className="me-2" />
                      )}
                      Generar
                    </Button>
                  </Col>
                </Row>
              </Form>
              {error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {generado && resumen && (
            <Row className="g-3 mb-4">
              <Col md={3}>
                <div className="dh-kpi">
                  <div className="text-muted small">Registros</div>
                  <strong>{resumen.total_registros}</strong>
                </div>
              </Col>
              <Col md={3}>
                <div className="dh-kpi">
                  <div className="text-muted small">DNI</div>
                  <strong>{resumen.total_dnis}</strong>
                </div>
              </Col>
              <Col md={3}>
                <div className="dh-kpi">
                  <div className="text-muted small">Con check-in / check-out</div>
                  <strong>
                    {resumen.total_con_check_in} / {resumen.total_con_check_out}
                  </strong>
                </div>
              </Col>
              <Col md={3}>
                <div className="dh-kpi">
                  <div className="text-muted small">Días hospedado (suma)</div>
                  <strong>{resumen.total_dias_hospedado}</strong>
                </div>
              </Col>
            </Row>
          )}

          {generado && (
            <Card className="dh-card">
              <Card.Header className="dh-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span>Detalle de estadías</span>
                {estadias.length > 0 && (
                  <Button
                    size="sm"
                    variant="light"
                    className="d-flex align-items-center"
                    onClick={exportarExcel}
                  >
                    <FaFileExcel className="me-1 text-success" /> Excel
                  </Button>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                {estadias.length === 0 ? (
                  <Alert variant="warning" className="m-4">
                    No hay registros de hospedaje para los filtros indicados.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover className="mb-0 dh-enhanced-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>DNI</th>
                          <th>Trabajador</th>
                          <th>Zona</th>
                          <th>Módulo</th>
                          <th>Habitación</th>
                          <th>Cama</th>
                          <th>Reserva</th>
                          <th>Check-in</th>
                          <th>Check-out</th>
                          <th>Días</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estadias.map((e, idx) => (
                          <tr key={e.asignacion_id ?? idx}>
                            <td>{idx + 1}</td>
                            <td>{e.dni ?? '—'}</td>
                            <td>{e.nombre_completo ?? '—'}</td>
                            <td>{e.zona ?? '—'}</td>
                            <td>{e.modulo ?? '—'}</td>
                            <td>{e.habitacion ?? '—'}</td>
                            <td>{e.cama ?? '—'}</td>
                            <td>{e.fecha_reserva_fmt ?? '—'}</td>
                            <td>{e.fecha_check_in_fmt ?? '—'}</td>
                            <td>{e.fecha_check_out_fmt ?? '—'}</td>
                            <td>
                              {e.dias_estadia != null ? e.dias_estadia : '—'}
                            </td>
                            <td>
                              <Badge bg={estadoBadge(e.estado_asignacion)}>
                                {e.estado_asignacion ?? '—'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
    </>
  );
};

export default ReporteHistorialHuesped;
